import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import SearchIcon from "@material-ui/icons/Search";
import InputBase from "@material-ui/core/InputBase";
import Chip from "@material-ui/core/Chip";
import Fab from "@material-ui/core/Fab";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsListCustom";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";
import { TagsFilter } from "../TagsFilter";
import { UsersFilter } from "../UsersFilter";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";

const useStyles = makeStyles((theme) => ({
  ticketsWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 0,
  },

  searchRow: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 1, 0.75, 1),
    gap: 6,
    backgroundColor: theme.palette.tabHeaderBackground,
    flexShrink: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },

  searchInputWrapper: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.total,
    borderRadius: 24,
    padding: "3px 10px",
    gap: 4,
  },

  searchIcon: {
    color: theme.palette.text.secondary,
    fontSize: 20,
    flexShrink: 0,
  },

  searchInput: {
    flex: 1,
    fontSize: "0.875rem",
  },

  chipsRow: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0.75, 1),
    gap: 6,
    backgroundColor: theme.palette.tabHeaderBackground,
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    overflowX: "auto",
    msOverflowStyle: "none",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
  },

  filterChip: {
    height: 28,
    fontSize: "0.78rem",
    cursor: "pointer",
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: "transparent",
    color: theme.palette.text.secondary,
    transition: "all 0.18s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "& .MuiChip-label": {
      paddingLeft: 10,
      paddingRight: 10,
      display: "flex",
      alignItems: "center",
    },
  },

  filterChipActive: {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: `#fff !important`,
    borderColor: `${theme.palette.primary.main} !important`,
    "&:hover": {
      backgroundColor: `${theme.palette.primary.dark} !important`,
    },
  },

  chipCount: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#e53935",
    color: "#fff",
    borderRadius: 10,
    padding: "0 5px",
    fontSize: "0.66rem",
    minWidth: 16,
    height: 16,
    marginLeft: 4,
    lineHeight: 1,
    fontWeight: 700,
  },

  chipCountPrimary: {
    background: "rgba(255,255,255,0.35)",
  },

  showAllBtn: {
    padding: 5,
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },

  showAllBtnActive: {
    color: theme.palette.primary.main,
  },

  listsWrapper: {
    flex: 1,
    overflow: "hidden",
    position: "relative",
  },

  listContainer: {
    height: "100%",
    overflow: "hidden",
  },

  fab: {
    position: "absolute",
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 10,
    width: 40,
    height: 40,
    minHeight: 40,
    boxShadow: theme.shadows[4],
  },
}));

const TicketsManagerTabs = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const searchInputRef = useRef();

  const [searchParam, setSearchParam] = useState("");
  const [activeFilter, setActiveFilter] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);

  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const { user } = useContext(AuthContext);
  const { profile } = user;

  const userQueueIds = user.queues.map((q) => q.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  useEffect(() => {
    if (location.state?.forceTabOpen && activeFilter !== location.state.forceTabOpen) {
      setActiveFilter(location.state.forceTabOpen);
      history.replace({
        pathname: location.pathname,
        search: location.search,
        state: { ...location.state, forceTabOpen: undefined },
      });
    }
  }, [location, activeFilter, history]);

  useEffect(() => {
    if (user.profile.toUpperCase() === "ADMIN") {
      setShowAllTickets(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchTimeout = useRef();

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();
    clearTimeout(searchTimeout.current);
    if (searchedTerm === "") {
      setSearchParam("");
      return;
    }
    searchTimeout.current = setTimeout(() => {
      setSearchParam(searchedTerm);
    }, 500);
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleSelectedTags = (selecteds) => {
    const tags = selecteds.map((t) => t.id);
    setSelectedTags(tags);
  };

  const handleSelectedUsers = (selecteds) => {
    const users = selecteds.map((t) => t.id);
    setSelectedUsers(users);
  };

  const ChipLabel = ({ text, count, isActive }) => (
    <span style={{ display: "flex", alignItems: "center" }}>
      {text}
      {count > 0 && (
        <span className={clsx(classes.chipCount, { [classes.chipCountPrimary]: isActive })}>
          {count}
        </span>
      )}
    </span>
  );

  return (
    <Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(ticket) => handleCloseOrOpenTicket(ticket)}
      />

      {/* Barra de busca sempre visível */}
      <div className={classes.searchRow}>
        <div className={classes.searchInputWrapper}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase
            className={classes.searchInput}
            inputRef={searchInputRef}
            placeholder={i18n.t("tickets.search.placeholder")}
            type="search"
            onChange={handleSearch}
          />
        </div>
        <Can
          role={user.profile}
          perform="tickets-manager:showall"
          yes={() => (
            <Tooltip title={showAllTickets ? "Mostrando todos os tickets" : "Ver apenas meus tickets"}>
              <IconButton
                size="small"
                className={clsx(classes.showAllBtn, { [classes.showAllBtnActive]: showAllTickets })}
                onClick={() => setShowAllTickets((prev) => !prev)}
              >
                <PeopleAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        />
        <TicketsQueueSelect
          selectedQueueIds={selectedQueueIds}
          userQueues={user?.queues}
          onChange={(values) => setSelectedQueueIds(values)}
        />
      </div>

      {/* Chips de filtro */}
      {!searchParam && (
        <div className={classes.chipsRow}>
          <Chip
            size="small"
            variant="outlined"
            label={
              <ChipLabel
                text={i18n.t("ticketsList.assignedHeader")}
                count={openCount}
                isActive={activeFilter === "open"}
              />
            }
            className={clsx(classes.filterChip, { [classes.filterChipActive]: activeFilter === "open" })}
            onClick={() => setActiveFilter("open")}
          />
          <Chip
            size="small"
            variant="outlined"
            label={
              <ChipLabel
                text={i18n.t("ticketsList.pendingHeader")}
                count={pendingCount}
                isActive={activeFilter === "pending"}
              />
            }
            className={clsx(classes.filterChip, { [classes.filterChipActive]: activeFilter === "pending" })}
            onClick={() => setActiveFilter("pending")}
          />
          <Chip
            size="small"
            variant="outlined"
            label={i18n.t("tickets.tabs.closed.title")}
            className={clsx(classes.filterChip, { [classes.filterChipActive]: activeFilter === "closed" })}
            onClick={() => setActiveFilter("closed")}
          />
        </div>
      )}

      {/* Filtros de tags/usuários na busca */}
      {searchParam && (
        <>
          <TagsFilter onFiltered={handleSelectedTags} />
          {profile === "admin" && (
            <UsersFilter onFiltered={handleSelectedUsers} />
          )}
        </>
      )}

      {/* Listas de tickets */}
      <div className={classes.listsWrapper}>
        <div
          className={classes.listContainer}
          style={!searchParam && activeFilter === "open" ? {} : { display: "none" }}
        >
          <TicketsList
            status="open"
            showAll={showAllTickets}
            selectedQueueIds={selectedQueueIds}
            updateCount={(val) => setOpenCount(val)}
          />
        </div>
        <div
          className={classes.listContainer}
          style={!searchParam && activeFilter === "pending" ? {} : { display: "none" }}
        >
          <TicketsList
            status="pending"
            selectedQueueIds={selectedQueueIds}
            updateCount={(val) => setPendingCount(val)}
            onStatusChange={setActiveFilter}
          />
        </div>
        <div
          className={classes.listContainer}
          style={!searchParam && activeFilter === "closed" ? {} : { display: "none" }}
        >
          <TicketsList
            status="closed"
            showAll={true}
            selectedQueueIds={selectedQueueIds}
          />
        </div>
        {searchParam && (
          <div className={classes.listContainer}>
            <TicketsList
              searchParam={searchParam}
              showAll={true}
              tags={selectedTags}
              users={selectedUsers}
              selectedQueueIds={selectedQueueIds}
            />
          </div>
        )}
      </div>

      {/* Botão flutuante novo ticket */}
      <Tooltip title={i18n.t("ticketsManager.buttons.newTicket")}>
        <Fab
          size="small"
          color="primary"
          className={classes.fab}
          onClick={() => setNewTicketModalOpen(true)}
        >
          <AddIcon />
        </Fab>
      </Tooltip>
    </Paper>
  );
};

export default TicketsManagerTabs;
